import React from 'react';
import {
  Box,
  Button,
  ButtonGroup,
  IconButton,
  Typography,
  Paper,
  Stack,
  Divider,
  Tooltip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Fab,
} from '@mui/material';
import {
  Brush as BrushIcon,
  Undo as UndoIcon,
  Clear as ClearIcon,
  Save as SaveIcon,
  Download as DownloadIcon,
  Upload as UploadIcon,
  Palette as PaletteIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { STROKE_COLORS, STROKE_WIDTH_OPTIONS } from '../utils/imageEditConstants';

interface ControlPanelProps {
  strokeWidth: number;
  strokeColor: string;
  canUndo: boolean;
  isLoading: boolean;
  onStrokeWidthChange: (width: number) => void;
  onStrokeColorChange: (color: string) => void;
  onUndo: () => void;
  onClear: () => void;
  onSave: () => void;
  onCancel?: () => void;
  isMobile?: boolean;
  isFixedBottom?: boolean;
  isModalFooter?: boolean;
}

export const ControlPanel: React.FC<ControlPanelProps> = ({
  strokeWidth,
  strokeColor,
  canUndo,
  isLoading,
  onStrokeWidthChange,
  onStrokeColorChange,
  onUndo,
  onClear,
  onSave,
  onCancel,
  isMobile = false,
  isFixedBottom = false,
  isModalFooter = false,
}) => {
  const theme = {
    accent: '#3B82F6',
    danger: '#EF4444',
    radius: 16,
    elevation: 8,
  };

  if (isModalFooter) {
    return (
      <Box sx={{ 
        display: 'flex', 
        flexDirection: { xs: 'column', sm: 'row' },
        gap: { xs: '12px', sm: '16px' },
        alignItems: { xs: 'stretch', sm: 'center' },
        justifyContent: 'space-between',
        width: '100%',
        maxWidth: '100%'
      }}>
        {/* Left Side - Action Buttons */}
        <Box sx={{ 
          display: 'flex', 
          gap: '8px',
          flexWrap: 'wrap',
          justifyContent: { xs: 'center', sm: 'flex-start' }
        }}>
          <Tooltip title="Rückgängig (Ctrl+Z)">
            <IconButton
              onClick={onUndo}
              disabled={!canUndo}
              size="small"
              sx={{
                backgroundColor: theme.accent,
                color: 'white',
                width: 40,
                height: 40,
                borderRadius: '8px',
                '&:hover': {
                  backgroundColor: theme.accent,
                  transform: 'scale(1.05)',
                },
                '&:disabled': {
                  backgroundColor: '#E5E7EB',
                  color: '#9CA3AF',
                },
                transition: 'all 0.2s',
              }}
            >
              <UndoIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          <Tooltip title="Löschen (Entf)">
            <IconButton
              onClick={onClear}
              size="small"
              sx={{
                backgroundColor: theme.danger,
                color: 'white',
                width: 40,
                height: 40,
                borderRadius: '8px',
                '&:hover': {
                  backgroundColor: theme.danger,
                  transform: 'scale(1.05)',
                },
                transition: 'all 0.2s',
              }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>

        {/* Center - Stroke Controls (Desktop) */}
        <Box sx={{ 
          display: { xs: 'flex', sm: 'flex' }, 
          justifyContent: 'center',
          alignItems: 'center',
          gap: '12px',
          flexWrap: 'wrap',
          order: { xs: 2, sm: 0 }
        }}>
          {/* Stroke Width */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <BrushIcon sx={{ color: theme.accent, fontSize: 16 }} />
            {STROKE_WIDTH_OPTIONS.map((width) => (
              <IconButton
                key={width}
                onClick={() => onStrokeWidthChange(width)}
                size="small"
                sx={{
                  width: 28,
                  height: 28,
                  backgroundColor: strokeWidth === width ? theme.accent : 'transparent',
                  border: `1px solid ${strokeWidth === width ? theme.accent : '#E5E7EB'}`,
                  borderRadius: '6px',
                  '&:hover': {
                    backgroundColor: strokeWidth === width ? theme.accent : '#F3F4F6',
                  },
                  transition: 'all 0.2s',
                }}
              >
                <Box
                  sx={{
                    width: width * 1.5,
                    height: width * 1.5,
                    backgroundColor: strokeWidth === width ? 'white' : theme.accent,
                    borderRadius: '50%',
                  }}
                />
              </IconButton>
            ))}
          </Box>

          {/* Color Palette */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <PaletteIcon sx={{ color: theme.accent, fontSize: 16 }} />
            {STROKE_COLORS.slice(0, 5).map((color, index) => (
              <IconButton
                key={index}
                onClick={() => onStrokeColorChange(color)}
                size="small"
                sx={{
                  width: 28,
                  height: 28,
                  backgroundColor: color,
                  border: strokeColor === color ? `2px solid ${theme.accent}` : '1px solid #E5E7EB',
                  borderRadius: '6px',
                  minWidth: 28,
                  '&:hover': {
                    transform: 'scale(1.05)',
                  },
                  transition: 'all 0.2s',
                }}
              >
                {strokeColor === color && (
                  <Box
                    sx={{
                      width: 6,
                      height: 6,
                      backgroundColor: color === '#FFFFFF' ? '#000' : '#fff',
                      borderRadius: '50%',
                    }}
                  />
                )}
              </IconButton>
            ))}
          </Box>
        </Box>

        {/* Right Side - Save Actions */}
        <Box sx={{ 
          display: 'flex', 
          gap: '8px',
          justifyContent: { xs: 'center', sm: 'flex-end' },
          order: { xs: 3, sm: 0 }
        }}>
          <Button
            variant="contained"
            onClick={onSave}
            disabled={isLoading}
            startIcon={<SaveIcon />}
            size="small"
            sx={{
              backgroundColor: theme.accent,
              color: 'white',
              borderRadius: '8px',
              padding: '8px 16px',
              fontSize: '14px',
              fontWeight: 600,
              textTransform: 'none',
              minWidth: '100px',
              '&:hover': {
                backgroundColor: theme.accent,
              },
              '&:disabled': {
                backgroundColor: '#E5E7EB',
                color: '#9CA3AF',
              },
              transition: 'all 0.2s',
            }}
          >
            {isLoading ? 'Speichert...' : 'Speichern'}
          </Button>

          {onCancel && (
            <Button
              onClick={onCancel}
              size="small"
              sx={{
                color: '#6B7280',
                borderRadius: '8px',
                padding: '8px 16px',
                fontSize: '14px',
                fontWeight: 600,
                textTransform: 'none',
                minWidth: '100px',
                '&:hover': {
                  backgroundColor: '#F3F4F6',
                },
                transition: 'all 0.2s',
              }}
            >
              Abbrechen
            </Button>
          )}
        </Box>
      </Box>
    );
  }

  if (isFixedBottom) {
    return (
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column',
        gap: '12px',
        width: '100%'
      }}>
        {/* Primary Action Buttons - Top Row */}
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'center',
          gap: '12px',
          flexWrap: 'wrap'
        }}>
          <Tooltip title="Rückgängig (Ctrl+Z)">
            <IconButton
              onClick={onUndo}
              disabled={!canUndo}
              sx={{
                backgroundColor: theme.accent,
                color: 'white',
                width: 48,
                height: 48,
                borderRadius: `${theme.radius}px`,
                '&:hover': {
                  backgroundColor: theme.accent,
                  transform: 'scale(1.05)',
                },
                '&:disabled': {
                  backgroundColor: '#E5E7EB',
                  color: '#9CA3AF',
                },
                transition: 'all 0.2s',
                boxShadow: `0 ${theme.elevation}px 20px rgba(0,0,0,0.1)`
              }}
            >
              <UndoIcon />
            </IconButton>
          </Tooltip>

          <Tooltip title="Löschen (Entf)">
            <IconButton
              onClick={onClear}
              sx={{
                backgroundColor: theme.danger,
                color: 'white',
                width: 48,
                height: 48,
                borderRadius: `${theme.radius}px`,
                '&:hover': {
                  backgroundColor: theme.danger,
                  transform: 'scale(1.05)',
                },
                transition: 'all 0.2s',
                boxShadow: `0 ${theme.elevation}px 20px rgba(0,0,0,0.1)`
              }}
            >
              <DeleteIcon />
            </IconButton>
          </Tooltip>
        </Box>

        {/* Stroke Controls */}
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'center',
          alignItems: 'center',
          gap: '8px',
          flexWrap: 'wrap'
        }}>
          {/* Stroke Width */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <BrushIcon sx={{ color: theme.accent, fontSize: 20 }} />
            {STROKE_WIDTH_OPTIONS.map((width) => (
              <IconButton
                key={width}
                onClick={() => onStrokeWidthChange(width)}
                sx={{
                  width: 32,
                  height: 32,
                  backgroundColor: strokeWidth === width ? theme.accent : 'transparent',
                  border: `2px solid ${strokeWidth === width ? theme.accent : '#E5E7EB'}`,
                  borderRadius: '8px',
                  '&:hover': {
                    backgroundColor: strokeWidth === width ? theme.accent : '#F3F4F6',
                    transform: 'scale(1.05)',
                  },
                  transition: 'all 0.2s',
                }}
              >
                <Box
                  sx={{
                    width: width * 2,
                    height: width * 2,
                    backgroundColor: strokeWidth === width ? 'white' : theme.accent,
                    borderRadius: '50%',
                  }}
                />
              </IconButton>
            ))}
          </Box>

          {/* Color Palette */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <PaletteIcon sx={{ color: theme.accent, fontSize: 20 }} />
            {STROKE_COLORS.slice(0, 6).map((color, index) => (
              <IconButton
                key={index}
                onClick={() => onStrokeColorChange(color)}
                sx={{
                  width: 32,
                  height: 32,
                  backgroundColor: color,
                  border: strokeColor === color ? `3px solid ${theme.accent}` : '2px solid #E5E7EB',
                  borderRadius: '8px',
                  '&:hover': {
                    transform: 'scale(1.1)',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                  },
                  transition: 'all 0.2s',
                }}
              >
                {strokeColor === color && (
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      backgroundColor: color === '#FFFFFF' ? '#000' : '#fff',
                      borderRadius: '50%',
                    }}
                  />
                )}
              </IconButton>
            ))}
          </Box>
        </Box>

        {/* Save Actions */}
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'center',
          gap: '12px'
        }}>
          <Button
            variant="contained"
            onClick={onSave}
            disabled={isLoading}
            startIcon={<SaveIcon />}
            sx={{
              backgroundColor: theme.accent,
              color: 'white',
              borderRadius: `${theme.radius}px`,
              padding: '12px 24px',
              fontSize: '14px',
              fontWeight: 600,
              textTransform: 'none',
              minWidth: '120px',
              '&:hover': {
                backgroundColor: theme.accent,
                transform: 'translateY(-2px)',
                boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)',
              },
              '&:disabled': {
                backgroundColor: '#E5E7EB',
                color: '#9CA3AF',
              },
              transition: 'all 0.2s',
            }}
          >
            {isLoading ? 'Speichert...' : 'Export'}
          </Button>

          {onCancel && (
            <Button
              onClick={onCancel}
              sx={{
                color: '#6B7280',
                borderRadius: `${theme.radius}px`,
                padding: '12px 24px',
                fontSize: '14px',
                fontWeight: 600,
                textTransform: 'none',
                minWidth: '120px',
                '&:hover': {
                  backgroundColor: '#F3F4F6',
                  transform: 'translateY(-2px)',
                },
                transition: 'all 0.2s',
              }}
            >
              Abbrechen
            </Button>
          )}
        </Box>
      </Box>
    );
  }

  if (isMobile) {
    return (
      <Paper elevation={2} sx={{ p: 2, height: '100%', overflow: 'auto' }}>
        <Stack spacing={1}>
          <Stack direction="row" spacing={1} justifyContent="space-between" flexWrap="wrap">
            <Button
              variant='contained'
              onClick={onSave}
              disabled={isLoading}
              startIcon={<SaveIcon />}
              size="small">
              {isLoading ? 'Speichert...' : 'Speichern'}
            </Button>

            {onCancel && (
              <Button onClick={onCancel} size="small">
                Abbrechen
              </Button>
            )}
          </Stack>

          <Divider />

          <Stack direction="row" spacing={1} flexWrap="wrap">
            <ButtonGroup size="small" orientation="horizontal">
              <Tooltip title="Rückgängig">
                <span>
                  <IconButton onClick={onUndo} disabled={!canUndo} size="small">
                    <UndoIcon />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Alles löschen">
                <IconButton onClick={onClear} size="small">
                  <ClearIcon />
                </IconButton>
              </Tooltip>
            </ButtonGroup>
          </Stack>

          <Divider />

          <Box>
            <Typography variant="body2" sx={{ mb: 1 }}>
              Pinselbreite
            </Typography>
            <FormControl fullWidth size="small">
              <Select
                value={strokeWidth}
                onChange={(e) => onStrokeWidthChange(Number(e.target.value))}>
                {STROKE_WIDTH_OPTIONS.map((width) => (
                  <MenuItem key={width} value={width}>
                    {width}px
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <Box>
            <Typography variant="body2" sx={{ mb: 1 }}>
              Farbe
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {STROKE_COLORS.map((color, index) => (
                <Box
                  key={index}
                  sx={{
                    width: 24,
                    height: 24,
                    backgroundColor: color,
                    border: strokeColor === color ? '2px solid #333' : '1px solid #ccc',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    '&:hover': {
                      transform: 'scale(1.1)',
                    },
                  }}
                  onClick={() => onStrokeColorChange(color)}
                />
              ))}
            </Box>
          </Box>
        </Stack>
      </Paper>
    );
  }

  return (
    <Paper elevation={2} sx={{ p: 2, height: '100%', overflow: 'auto' }}>
      <Stack spacing={2}>
        <Box>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Bearbeitung
          </Typography>

          <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
            <Tooltip title="Rückgängig">
              <span>
                <IconButton onClick={onUndo} disabled={!canUndo}>
                  <UndoIcon />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Alles löschen">
              <IconButton onClick={onClear}>
                <ClearIcon />
              </IconButton>
            </Tooltip>
          </Stack>
        </Box>

        <Divider />

        <Box>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Pinselbreite
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
            {STROKE_WIDTH_OPTIONS.map((width) => (
              <IconButton
                key={width}
                onClick={() => onStrokeWidthChange(width)}
                sx={{
                  width: 40,
                  height: 40,
                  border: strokeWidth === width ? '2px solid #1976d2' : '1px solid #ccc',
                  borderRadius: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}>
                <Box
                  sx={{
                    width: width * 1.5,
                    height: width * 1.5,
                    backgroundColor: strokeWidth === width ? '#1976d2' : '#666',
                    borderRadius: '50%',
                  }}
                />
              </IconButton>
            ))}
          </Box>
        </Box>

        <Box>
          <Typography variant="body2" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
            <PaletteIcon fontSize="small" />
            Farbe
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {STROKE_COLORS.map((color, index) => (
              <Box
                key={index}
                sx={{
                  width: 30,
                  height: 30,
                  backgroundColor: color,
                  border: strokeColor === color ? '2px solid #333' : '1px solid #ccc',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.2s',
                  '&:hover': {
                    transform: 'scale(1.1)',
                    border: '2px solid #666',
                  },
                }}
                onClick={() => onStrokeColorChange(color)}>
                {strokeColor === color && (
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      backgroundColor: color === '#FFFFFF' ? '#000' : '#fff',
                      borderRadius: '50%',
                    }}
                  />
                )}
              </Box>
            ))}
          </Box>
        </Box>

        <Divider />

        <Box>
          <Stack spacing={1} direction="column">
            <Button
              variant='contained'
              onClick={onSave}
              disabled={isLoading}
              startIcon={<SaveIcon />}
              size="small"
              fullWidth>
              {isLoading ? 'Speichert...' : 'Speichern'}
            </Button>

            {onCancel && (
              <Button onClick={onCancel} size="small" fullWidth>
                Abbrechen
              </Button>
            )}
          </Stack>
        </Box>
      </Stack>
    </Paper>
  );
};
